import React, { useEffect, useRef, useState } from 'react';
import CursorOrbCard from './CursorOrbCard';
import MagneticButton from './MagneticButton';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Paper,
  Avatar,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Storage,
  Cloud,
  CheckCircle,
  Security,
  Support,
  Architecture,
  CloudSync,
  Shield,
  TrendingUp,
  Rocket,
  MonetizationOn,
  CloudDone,
  ArrowForward,
  CompareArrows,
  ExpandMore,
  ExpandLess,
  SyncAlt,
  Lock,
  Speed,
} from '@mui/icons-material';
import { useDetailModal } from '../hooks/useDetailModal';
import { useDemoModal } from '../hooks/useDemoModal';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';

function DeploymentOptions() {
  const { isLight } = useThemeMode();
  const { openDetailModal } = useDetailModal();
  const { openModal } = useDemoModal();
  const [showComparison, setShowComparison] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);
  const { ref: deployRef, revealStyle } = useScrollReveal();

  const currentDate = "2025-07-28 19:15:41";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleCards((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );
    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const handleLearnMore = (type) => {
    if (type === 'on-premises') {
      openDetailModal('on-premise', {
        title: "On-Premises Deployment Details",
        subtitle: "Enterprise-grade control with maximum security",
        sections: [
          {
            heading: "Technical Requirements",
            content: "Our on-premises solution requires a minimum of 16GB RAM, quad-core server with 500GB storage, and support for containerization. Compatible with major Linux distributions and Windows Server 2019+."
          },
          {
            heading: "Implementation Process",
            content: "Implementation typically takes 2-4 weeks, including environment setup, data migration, and integration with existing systems. Our technical team works directly with your IT department throughout the process."
          },
          {
            heading: "Security Compliance",
            content: "On-premise deployment helps meet regulatory requirements like GDPR, HIPAA, SOC2, and industry-specific regulations by keeping data within your secure environment. All security policies can be customized to match your organization's standards."
          },
          {
            heading: "Cost Structure",
            content: "Annual licensing model based on number of integrations and data volume. Implementation services are priced separately. Total cost of ownership includes software licensing, infrastructure, and maintenance."
          }
        ],
        lastUpdated: currentDate,
        ctaText: "Schedule Technical Consultation",
        ctaAction: () => openModal()
      });
    } else if (type === 'hybrid') {
      openDetailModal('hybrid', {
        title: "Hybrid Deployment Details",
        subtitle: "Best of both worlds — data on-prem, platform in the cloud",
        sections: [
          {
            heading: "Architecture Overview",
            content: "Your sensitive data stays within your on-premises infrastructure while Alphanetix's processing engine, workflow designer, and management console run in our secure cloud. A lightweight agent installed in your environment handles all data access with encrypted tunnels."
          },
          {
            heading: "Data Sovereignty",
            content: "All business data, credentials, and sensitive records remain behind your firewall. Only workflow metadata and execution logs are synced to the cloud. You retain full control over data residency and compliance requirements."
          },
          {
            heading: "Implementation",
            content: "Setup takes 1-2 weeks. We deploy a secure agent in your environment, configure encrypted channels, and connect your data sources. Cloud-side features are available immediately, with on-prem data access activated upon agent deployment."
          },
          {
            heading: "Pricing",
            content: "Hybrid plans can be customized to your use, combining cloud subscription with a one-time agent setup fee. Volume discounts are available for enterprise deployments across multiple data centers."
          }
        ],
        lastUpdated: currentDate,
        ctaText: "Discuss Hybrid Setup",
        ctaAction: () => openModal()
      });
    } else {
      openDetailModal('cloud', {
        title: "Cloud Subscription Details",
        subtitle: "Scalable, flexible, and instantly available",
        sections: [
          {
            heading: "Service Tiers",
            content: "Available in Standard (up to 10 integrations), Professional (up to 50 integrations), and Enterprise (unlimited integrations) plans. Each tier includes different levels of support, performance, and additional features."
          },
          {
            heading: "Data Security",
            content: "All data is encrypted at rest and in transit using AES-256 encryption. Daily backups with 30-day retention and optional geo-redundant storage. SOC2 Type II enabled infrastructure with regular security audits."
          },
          {
            heading: "Performance & SLAs",
            content: "99.9% uptime guarantee with automatic failover. Maximum response time of 1 hour for critical issues on Enterprise plans. Automated monitoring and alerting to detect and resolve issues before they affect your operations."
          },
          {
            heading: "Pricing Structure",
            content: "Monthly subscription based on number of integrations and data volume. Discounts available for annual commitments. Transparent pricing with no hidden fees, including all updates and support in the subscription cost."
          }
        ],
        lastUpdated: currentDate,
        ctaText: "Book a Demo",
        ctaAction: () => openModal()
      });
    }
  };

  const deploymentOptions = [
    {
      title: 'On-Premises',
      subtitle: 'Enterprise Control',
      icon: <Storage />,
      color: isLight ? '#6366f1' : '#047857',
      iconBg: isLight ? 'rgba(99, 102, 241, 0.06)' : 'rgba(4, 120, 87, 0.2)',
      description:
        'Deploy within your infrastructure for maximum control, data sovereignty, and compliance.',
      features: [
        { icon: <Shield />, text: 'Complete Data Sovereignty' },
        { icon: <Architecture />, text: 'Infrastructure Integration' },
        { icon: <Security />, text: 'Enhanced Security Controls' },
        { icon: <Support />, text: 'Dedicated Support' },
      ],
      pricing: 'Enterprise Pricing',
      popular: false,
      buttonAction: () => handleLearnMore('on-premises'),
      ctaText: 'Get Enterprise Quote',
    },
    {
      title: 'Hybrid',
      subtitle: 'Best of Both Worlds',
      icon: <SyncAlt />,
      color: isLight ? '#6366f1' : '#047857',
      iconBg: isLight ? 'rgba(99, 102, 241, 0.06)' : 'rgba(56, 189, 248, 0.2)',
      description: 'Keep your data on-premises while running platform features in the cloud. Maximum security with cloud convenience.',
      features: [
        { icon: <Lock />, text: 'Data Stays On-Prem' },
        { icon: <Cloud />, text: 'Cloud-Powered Platform' },
        { icon: <Speed />, text: 'Fast Setup (1-2 Weeks)' },
        { icon: <Shield />, text: 'Compliance Ready' },
      ],
      pricing: 'Enterprise Pricing',
      popular: false,
      buttonAction: () => handleLearnMore('hybrid'),
      ctaText: 'Explore Hybrid',
    },
    {
      title: 'Cloud SaaS',
      subtitle: 'Instant Deployment',
      icon: <Cloud />,
      color: isLight ? '#6366f1' : '#10b981',
      iconBg: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(16, 185, 129, 0.2)',
      description:
        'Fully managed cloud solution. Scalable, secure, and maintenance-free deployment for your convenience.',
      features: [
        { icon: <Rocket />, text: 'Instant Deployment' },
        { icon: <TrendingUp />, text: 'Auto-Scaling' },
        { icon: <CloudSync />, text: 'Zero Maintenance' },
        { icon: <MonetizationOn />, text: 'Flexible Pricing' },
      ],
      pricing: 'From $99/month',
      buttonAction: () => handleLearnMore('cloud'),
      ctaText: 'Start Free Trial',
    },
  ];

  const comparisonFeatures = [
    { feature: 'Deployment Time', onPremise: '2-4 Weeks', hybrid: '1-2 Weeks', cloud: 'Instant' },
    { feature: 'Data Control', onPremise: 'Full Control', hybrid: 'Full Control', cloud: 'Managed' },
    { feature: 'Maintenance', onPremise: 'Self-Managed', hybrid: 'Mostly Managed', cloud: 'Fully Managed' },
    { feature: 'Scalability', onPremise: 'Manual', hybrid: 'Cloud Auto-Scale', cloud: 'Automatic' },
    { feature: 'Compliance', onPremise: 'Custom', hybrid: 'Custom + Standard', cloud: 'Standard' },
    { feature: 'Cost Model', onPremise: 'Annual License', hybrid: 'Subscription+', cloud: 'Subscription' },
  ];

  return (
    <Box
      component="section"
      id="deployment"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(45deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container ref={deployRef} maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, px: { xs: 2, sm: 0 }, ...revealStyle(0) }}>
          <Chip
            icon={<CloudDone sx={{ fontSize: '1rem' }} />}
            label="DEPLOYMENT OPTIONS"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(56, 189, 248, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(56, 189, 248, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': { color: isLight ? '#6366f1' : '#38bdf8' },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Flexible{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                  : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Deployment
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Choose the model that fits your security, compliance, and operational
            needs.
          </Typography>
        </Box>

        {/* Cards */}
        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 5 } }}>
          {deploymentOptions.map((option, index) => (
            <Grid item xs={12} md={4} key={index}>
              <CursorOrbCard color={option.color} isLight={isLight}>
              <Card
                ref={(el) => (cardRefs.current[index] = el)}
                data-index={index}
                elevation={0}
                sx={{
                  height: '100%',
                  background: isLight
                    ? 'rgba(255,255,255,0.4)'
                    : 'rgba(15,23,42,0.4)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: option.popular
                    ? isLight ? `2px solid rgba(99, 102, 241, 0.3)` : `2px solid ${option.color}60`
                    : isLight ? '1px solid rgba(255, 255, 255, 0.5)' : `1px solid ${option.color}20`,
                  borderRadius: 3,
                  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                  transition: 'all 0.5s ease',
                  opacity: visibleCards.includes(index) ? 1 : 0,
                  transform: visibleCards.includes(index)
                    ? 'translateY(0)'
                    : 'translateY(30px)',
                  '&:hover': {
                    borderColor: `${option.color}40`,
                    boxShadow: `0 12px 40px ${option.color}15`,
                    transform: 'translateY(-4px)',
                    '& .deploy-card-icon': {
                      animation: 'icon-pop 0.4s ease forwards',
                    },
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  {/* Title row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Avatar
                      className="deploy-card-icon"
                      sx={{
                        width: 44,
                        height: 44,
                        background: option.iconBg,
                        color: option.color,
                        border: `2px solid ${option.color}30`,
                      }}
                    >
                      {React.cloneElement(option.icon, {
                        sx: { fontSize: '1.3rem' },
                      })}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          color: option.color,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          display: 'block',
                        }}
                      >
                        {option.subtitle}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: 800,
                          color: isLight ? '#0f172a' : '#fff',
                          lineHeight: 1.1,
                        }}
                      >
                        {option.title}
                      </Typography>
                    </Box>
                    
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: isLight
                        ? 'rgba(15,23,42,0.7)'
                        : 'rgba(248,250,252,0.7)',
                      mb: 2.5,
                      lineHeight: 1.6,
                    }}
                  >
                    {option.description}
                  </Typography>

                  {/* Features list */}
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {option.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 0.5,
                          px: 1,
                          mx: -1,
                          borderRadius: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isLight
                              ? `${option.color}06`
                              : `${option.color}08`,
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            background: option.iconBg,
                            color: option.color,
                          }}
                        >
                          {React.cloneElement(feature.icon, {
                            sx: { fontSize: '0.9rem' },
                          })}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isLight
                              ? '#1e293b'
                              : 'rgba(248,250,252,0.9)',
                            fontSize: '0.9rem',
                          }}
                        >
                          {feature.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  {/* CTA */}
                  <MagneticButton>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={option.buttonAction}
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      borderRadius: 2,
                      background: option.color,
                      color: 'white',
                      boxShadow: `0 4px 16px ${option.color}30`,
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-75%',
                        width: '50%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transform: 'skewX(-20deg)',
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 24px ${option.color}40`,
                        background: option.color,
                        '&::before': {
                          animation: 'shine-sweep 0.6s ease forwards',
                        },
                      },
                      '&:active': {
                        transform: 'translateY(0) scale(0.97)',
                        transition: 'transform 0.1s ease',
                      },
                    }}
                  >
                    {option.ctaText}
                  </Button>
                  </MagneticButton>
                </CardContent>
              </Card>
              </CursorOrbCard>
            </Grid>
          ))}
        </Grid>

        {/* Compare button */}
        <Box sx={{ textAlign: 'center', mb: showComparison ? 3 : 0 }}>
          <Button
            variant="outlined"
            onClick={() => setShowComparison(!showComparison)}
            startIcon={<CompareArrows />}
            endIcon={showComparison ? <ExpandLess /> : <ExpandMore />}
            sx={{
              borderColor: isLight
                ? 'rgba(99, 102, 241, 0.3)'
                : 'rgba(56, 189, 248, 0.3)',
              color: isLight ? '#818cf8' : '#38bdf8',
              borderRadius: '50px',
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                borderColor: isLight
                  ? 'rgba(99, 102, 241, 0.5)'
                  : 'rgba(56, 189, 248, 0.5)',
                background: isLight
                  ? 'rgba(99, 102, 241, 0.05)'
                  : 'rgba(56, 189, 248, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.96)',
                transition: 'transform 0.1s ease',
              },
            }}
          >
            {showComparison ? 'Hide' : 'Compare'} Options
          </Button>
        </Box>

        {/* Comparison Table */}
        <Collapse in={showComparison} timeout={400}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: isLight
                ? 'rgba(255,255,255,0.4)'
                : 'rgba(15,23,42,0.4)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.5)'
                : '1px solid rgba(56, 189, 248, 0.2)',
              boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: isLight
                      ? 'rgba(15,23,42,0.5)'
                      : 'rgba(248,250,252,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Feature
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Storage
                    sx={{
                      color: isLight ? '#6366f1' : '#047857',
                      fontSize: '0.9rem',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: isLight ? '#6366f1' : '#047857',
                    }}
                  >
                    On-Premises
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <SyncAlt
                    sx={{
                      color: isLight ? '#6366f1' : '#38bdf8',
                      fontSize: '0.9rem',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: isLight ? '#6366f1' : '#38bdf8',
                    }}
                  >
                    Hybrid
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Cloud
                    sx={{
                      color: isLight ? '#6366f1' : '#10b981',
                      fontSize: '0.9rem',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: isLight ? '#6366f1' : '#10b981',
                    }}
                  >
                    Cloud
                  </Typography>
                </Box>
              </Grid>

              {comparisonFeatures.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                    <Divider sx={{ opacity: 0.15 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isLight ? '#1e293b' : '#fff',
                        fontSize: '0.85rem',
                      }}
                    >
                      {item.feature}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={item.onPremise}
                      size="small"
                      sx={{
                        background: isLight
                          ? 'rgba(35,57,93,0.06)'
                          : 'rgba(4,120,87,0.15)',
                        color: isLight ? '#4f46e5' : '#047857',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: isLight
                          ? '1px solid rgba(35,57,93,0.12)'
                          : '1px solid rgba(4,120,87,0.25)',
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={item.hybrid}
                      size="small"
                      sx={{
                        background: isLight
                          ? 'rgba(37,99,235,0.08)'
                          : 'rgba(56,189,248,0.15)',
                        color: isLight ? '#6366f1' : '#38bdf8',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: isLight
                          ? '1px solid rgba(37,99,235,0.15)'
                          : '1px solid rgba(56,189,248,0.25)',
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={item.cloud}
                      size="small"
                      sx={{
                        background: isLight
                          ? 'rgba(99, 102, 241,0.08)'
                          : 'rgba(16,185,129,0.15)',
                        color: isLight ? '#4f46e5' : '#10b981',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: isLight
                          ? '1px solid rgba(99, 102, 241,0.15)'
                          : '1px solid rgba(16,185,129,0.25)',
                      }}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Paper>
        </Collapse>
      </Container>
    </Box>
  );
}

export default DeploymentOptions;
